import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../config/axiosConfig';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../components/ToastProvider';
import { motion } from 'framer-motion';

const ContactUs = () => {
    const addToast = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        restaurantName: '',
        ownerName: '',
        email: '',
        contactNo: '',
        city: '',
        state: '',
        pincode: ''
    });

    const [contactError, setContactError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'contactNo') {
            const digitsOnly = value.replace(/\D/g, '');
            if (digitsOnly.length <= 12) {
                setForm((prev) => ({ ...prev, [name]: digitsOnly }));
                setContactError('');
            } else {
                setContactError('Contact number should not exceed 12 digits');
                return;
            }
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if ((form.contactNo || '').replace(/\D/g, '').length > 12) {
            setContactError('Contact number should not exceed 12 digits');
            return;
        }
        setContactError('');
        setSubmitting(true);
        try {
            await axios.post(API_ENDPOINTS.CONTACT_LEADS_SUBMIT, form);
            setSubmitted(true);
            setForm({ restaurantName: '', ownerName: '', email: '', contactNo: '', city: '', state: '', pincode: '' });
            addToast('Thank you! We will contact you soon.', 'success');
        } catch (err) {
            addToast(err.response?.data?.message || 'Failed to submit. Please try again.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass = "w-full px-3 py-2 sm:px-4 sm:py-2.5 bg-stone-50 border border-stone-200 rounded-lg sm:rounded-xl text-stone-900 text-sm placeholder-stone-400 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors";
    const labelClass = "block text-xs sm:text-sm font-medium text-stone-700 mb-0.5 sm:mb-1";

    if (submitted) {
        return (
            <div className="min-h-screen bg-stone-50 text-stone-900 overflow-hidden flex items-center justify-center px-4">
                <div className="fixed inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-b from-amber-100/40 via-transparent to-transparent" />
                    <div className="absolute top-0 right-0 w-[80vmax] h-[80vmax] bg-amber-200/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-[60vmax] h-[60vmax] bg-amber-100/40 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
                </div>
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="relative z-10 max-w-md w-full bg-white rounded-2xl p-8 border border-stone-200/80 shadow-lg text-center"
                >
                    <div className="text-5xl mb-4">✅</div>
                    <h2 className="text-2xl font-bold text-stone-900 mb-2">Thank You!</h2>
                    <p className="text-stone-600 mb-6">We have received your details. Our team will contact you soon to get you started with your free trial.</p>
                    <Link
                        to="/"
                        className="inline-block bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-amber-500/25"
                    >
                        Back to Home
                    </Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-stone-50 text-stone-900 overflow-hidden">
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-b from-amber-100/40 via-transparent to-transparent" />
                <div className="absolute top-0 right-0 w-[80vmax] h-[80vmax] bg-amber-200/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-[60vmax] h-[60vmax] bg-amber-100/40 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(251,191,36,0.12),transparent)]" />
            </div>

            <header className="relative z-50 border-b border-stone-200/80 bg-white/70 backdrop-blur-sm">
                <div className="max-w-2xl mx-auto px-3 sm:px-6 py-2.5 sm:py-4">
                    <div className="flex items-center justify-between gap-2 min-w-0">
                        <Link to="/" className="flex items-center gap-2 sm:gap-3 text-stone-700 hover:text-stone-900 transition-colors min-w-0 overflow-hidden">
                            <img src="/logo-bright.svg" alt="RestroAI" className="h-6 sm:h-8 md:h-9 w-auto max-w-[80px] sm:max-w-[110px] md:max-w-none object-contain object-left flex-shrink-0" />

                        </Link>
                        <nav className="flex items-center gap-1.5 sm:gap-2 md:gap-3 flex-shrink-0">
                            <Link
                                to="/"
                                className="inline-flex items-center justify-center text-xs sm:text-sm font-medium text-stone-700 hover:text-stone-900 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-full bg-amber-50 border border-amber-200 hover:border-amber-300 hover:bg-amber-100/80 transition-all duration-300 whitespace-nowrap min-h-[2.25rem] sm:min-h-[2.5rem]"
                            >
                                Home
                            </Link>
                            <Link
                                to="/restaurants"
                                className="inline-flex items-center justify-center text-xs sm:text-sm font-medium text-stone-700 hover:text-stone-900 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-full bg-amber-50 border border-amber-200 hover:border-amber-300 hover:bg-amber-100/80 transition-all duration-300 whitespace-nowrap min-h-[2.25rem] sm:min-h-[2.5rem]"
                            >
                                Restaurants
                            </Link>
                            <Link
                                to="/pocketbillingai"
                                className="hidden sm:inline-flex items-center justify-center text-xs sm:text-sm font-medium text-indigo-700 hover:text-indigo-900 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-full bg-indigo-50 border border-indigo-200 hover:border-indigo-300 transition-all duration-300 whitespace-nowrap min-h-[2.25rem] sm:min-h-[2.5rem]"
                            >
                                PocketBillingAI
                            </Link>
                            <Link
                                to="/login"
                                className="inline-flex items-center justify-center text-xs sm:text-sm font-semibold text-amber-600 hover:text-amber-700 px-2.5 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 rounded-full bg-amber-50 border border-amber-200 hover:border-amber-300 transition-all duration-300 whitespace-nowrap min-h-[2.25rem] sm:min-h-[2.5rem]"
                            >
                                Login
                            </Link>
                        </nav>
                    </div>
                </div>
            </header>

            <main className="relative z-10 max-w-lg mx-auto px-3 sm:px-6 py-6 sm:py-10 md:py-14">
                <motion.div
                    className="text-center mb-4 sm:mb-6 md:mb-8"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <p className="text-amber-600 text-xs sm:text-sm font-semibold tracking-widest uppercase mb-1 sm:mb-2">Get started</p>
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-900 mb-1 sm:mb-2">Start Your Free Trial</h1>
                    <p className="text-stone-600 text-sm sm:text-base">Share your restaurant details and we&apos;ll get in touch.</p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-stone-200/80 shadow-sm"
                >
                    <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                        <div>
                            <label className={labelClass}>Owner Name *</label>
                            <input type="text" name="ownerName" value={form.ownerName} onChange={handleChange} required
                                className={inputClass} placeholder="Your name" />
                        </div>
                        <div>
                            <label className={labelClass}>Restaurant Name *</label>
                            <input type="text" name="restaurantName" value={form.restaurantName} onChange={handleChange} required
                                className={inputClass} placeholder="Your restaurant name" />
                        </div>
                        <div>
                            <label className={labelClass}>Email</label>
                            <input type="email" name="email" value={form.email} onChange={handleChange}
                                className={inputClass} placeholder="you@example.com (optional)" />
                        </div>
                        <div>
                            <label className={labelClass}>Contact Number *</label>
                            <input type="tel" name="contactNo" value={form.contactNo} onChange={handleChange} required maxLength={12}
                                className={`${inputClass} ${contactError ? 'border-red-400 focus:ring-red-500' : ''}`} placeholder="Max 12 digits" />
                            {contactError && <p className="text-red-600 text-xs sm:text-sm mt-0.5 sm:mt-1">{contactError}</p>}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                            <div className="sm:col-span-2">
                                <label className={labelClass}>City *</label>
                                <input type="text" name="city" value={form.city} onChange={handleChange} required
                                    className={inputClass} placeholder="City" />
                            </div>
                            <div>
                                <label className={labelClass}>State *</label>
                                <input type="text" name="state" value={form.state} onChange={handleChange} required
                                    className={inputClass} placeholder="State" />
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>Pincode</label>
                            <input type="text" name="pincode" value={form.pincode} onChange={handleChange} maxLength={10}
                                className={inputClass} placeholder="Pincode (optional)" />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-amber-500 hover:bg-amber-600 text-white py-2.5 px-4 sm:py-3 sm:px-6 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 shadow-lg shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed mt-4 sm:mt-6"
                        >
                            {submitting ? 'Submitting...' : 'Submit'}
                        </button>
                    </form>
                </motion.div>
            </main>
        </div>
    );
};

export default ContactUs;
